import axios, { AxiosResponse, AxiosRequestConfig, AxiosError } from 'axios';
import { handleAuthError } from '../utils/authUtils';
import { REFRESH_TOKEN } from './endpoints';

axios.defaults.baseURL = process.env.REACT_APP_API_URL;

// --- Configuración de Token ---
const authUser: any = localStorage.getItem("authUser");
const token = JSON.parse(authUser) ? JSON.parse(authUser).token : null;

if (token)
  axios.defaults.headers.common["Authorization"] = "Bearer " + token;

let isRefreshing = false;
let failedQueue: Array<{
  resolve: (value?: any) => void;
  reject: (reason?: any) => void;
}> = [];

const processQueue = (error: any, token: string | null = null) => {
  failedQueue.forEach(({ resolve, reject }) => {
    if (error) {
      reject(error);
    } else {
      resolve(token);
    }
  });
  
  failedQueue = [];
};

axios.interceptors.response.use(
    function (response) {
      return response.data ? response.data : response;
    },
    async function (error: AxiosError) {
      const status = error.response ? error.response.status : null;
      const data: any = error.response ? error.response.data : null;
      const originalRequest = error.config as AxiosRequestConfig & { _retry?: boolean };

      const isRefreshRequest = originalRequest.url?.includes(REFRESH_TOKEN);
      const isLoginRequest = originalRequest.url?.includes('/auth/login');

      if (status === 401 && originalRequest && !originalRequest._retry && !isRefreshRequest && !isLoginRequest) {
        if (isRefreshing) {
          return new Promise((resolve, reject) => {
            failedQueue.push({ resolve, reject });
          }).then(() => {
            return axios(originalRequest);
          }).catch((err) => {
            return Promise.reject(err);
          });
        }

        originalRequest._retry = true;
        isRefreshing = true;

        try {
          const response = await axios.post(REFRESH_TOKEN, {}, {
            withCredentials: true
          });

          if (response.data && response.data.token) {
            const newToken = response.data.token;

            const authUser = localStorage.getItem("authUser");
            if (authUser) {
              const user = JSON.parse(authUser);
              user.token = newToken;
              localStorage.setItem("authUser", JSON.stringify(user));
            }

            axios.defaults.headers.common["Authorization"] = "Bearer " + newToken;

            processQueue(null, newToken);

            return axios(originalRequest);
          }
        } catch (refreshError) {
          processQueue(refreshError, null);

          const currentAuth = localStorage.getItem("authUser");
          if (currentAuth) {
            handleAuthError();
          }
          
          return Promise.reject(refreshError);
        } finally {
          isRefreshing = false;
        }
      }

      let message = error.message || "An unexpected error occurred";

      if (data) {
          if (typeof data === 'string') {
              message = data;
          } else if (typeof data === 'object' && data.message) {
              message = data.message;
          }
      }

      if (status === 500 && data && data.stack && data.stack.includes("ER_DUP_ENTRY")) {
        message = "El registro ya existe (RUC duplicado).";
      }

      error.message = message;

      if (status === 403) {
          const currentAuth = localStorage.getItem("authUser");
          if (currentAuth) {
              handleAuthError();
          }
      }

      return Promise.reject(error);
    }
);

const setAuthorization = (token : string) => {
  axios.defaults.headers.common["Authorization"] = "Bearer " + token;
};

class APIClient {
  get = (url: string, config?: AxiosRequestConfig): Promise<AxiosResponse> => {
    return axios.get(url, config);
  };

  create = (url: string, data: any, config?: AxiosRequestConfig): Promise<AxiosResponse> => {
    return axios.post(url, data, config);
  };

  download = (url: string, data: any): Promise<AxiosResponse<Blob>> => {
    return axios.post(url, data, {
      responseType: 'blob',
    });
  };

  update = (url: string, data: any): Promise<AxiosResponse> => {
    return axios.patch(url, data);
  };

  put = (url: string, data: any, config?: AxiosRequestConfig): Promise<AxiosResponse> => {
    return axios.put(url, data, config);
  };

  delete = (url: string, config?: AxiosRequestConfig): Promise<AxiosResponse> => {
    return axios.delete(url, { ...config });
  };
}

const getLoggedinUser = () => {
  const user = localStorage.getItem("authUser");
  if (!user) {
    return null;
  } else {
    return JSON.parse(user);
  }
};

export { APIClient, setAuthorization, getLoggedinUser };
